
import React, { createContext, useContext, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AuthContextType, User } from "./auth/types";
import { useAuthState } from "./auth/useAuthState";
import { loginUser, logoutUser } from "./auth/authOperations";
import { hybridLoginUser, hybridLogoutUser } from "./auth/hybridAuthOperations";
import { savePackerRelations } from "./auth/authUtils";

// Extended context type with session validity
interface ExtendedAuthContextType extends AuthContextType {
  isSessionValid: boolean;
  isValidating: boolean;
}

const AuthContext = createContext<ExtendedAuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const { 
    user, 
    setUser, 
    isAuthenticated, 
    setIsAuthenticated, 
    selectedPackers, 
    setSelectedPackers,
    isSessionValid,
    isValidating
  } = useAuthState();

  // Invalidate all queries when session is restored automatically
  useEffect(() => {
    const handleRestore = () => {
      console.log("[AuthProvider] Session restored, invalidating all queries");
      queryClient.invalidateQueries();
    };
    window.addEventListener('session-restored', handleRestore);
    return () => window.removeEventListener('session-restored', handleRestore);
  }, [queryClient]);

  const updateSelectedPackers = (packers: string[]) => {
    localStorage.setItem('selectedPackers', JSON.stringify(packers));
    setSelectedPackers(packers);
    
    // If a user is logged in, update the packer relations in the database
    if (user) {
      console.log("Updating packer relations for user:", user.id, "name:", user.name, "packers:", packers);
      savePackerRelations(user.id, packers, user.name);
    }
  };

  const login = async (username: string, password: string, packers: string[] = []): Promise<boolean> => {
    return hybridLoginUser(username, password, packers, setUser, setIsAuthenticated, setSelectedPackers);
  };

  const logout = () => {
    hybridLogoutUser(user, setUser, setIsAuthenticated, setSelectedPackers);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      isAuthenticated, 
      selectedPackers, 
      updateSelectedPackers,
      isSessionValid,
      isValidating
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// HMR: dit module maakt een React Context aan. Bij een hot update ontstaat er
// een NIEUWE context-instantie terwijl de al gemounte AuthProvider nog de oude
// gebruikt -> useAuth() krijgt undefined ("must be used within an AuthProvider")
// en het scherm wordt blank. Forceer daarom een volledige reload.
if (import.meta.hot) {
  import.meta.hot.invalidate();
}

// Re-export types for convenience
export type { UserRole, User } from "./auth/types";

